import {
  normalizeRecordTimePunchPayload,
  type TimePunchType,
} from "../../../../packages/shared/src/timeclock.ts";

// Timeclock transport — shared normalizer (fail-closed client contract) → hosted
// public.record_time_punch(...). Online-only by assignment: there is NO punch queue on web;
// the panel says so honestly instead of pretending. Web punches carry notes: null and empty
// metadata BY DESIGN — no free text can ride a punch from this surface. Server auth, account
// permission, and final validation remain the RPC's job (the normalizer is not authorization).

export type { TimePunchType };

/** Structural slice of supabase-js we call — keeps tests free of the generated Json typing. */
export interface TimePunchRpcClient {
  rpc(
    fn: string,
    args: Record<string, unknown>,
  ): Promise<{ data: unknown; error: { message: string } | null }>;
}

export interface SubmitPunchInput {
  accountId: string;
  punchType: TimePunchType;
  deviceId: string;
  appVersion: string;
  routeAssignmentId: string | null;
  lat: number | null;
  lng: number | null;
  gpsAccuracyMeters: number | null;
  gpsPermission: string;
}

export type SubmitPunchResult = { ok: true } | { ok: false; reason: string };

const SAFE_SUBMIT_FAILURE = "Could not record the punch. Check your connection and try again.";

export async function submitTimePunch(
  client: TimePunchRpcClient,
  input: SubmitPunchInput,
  deviceTime: string = new Date().toISOString(),
): Promise<SubmitPunchResult> {
  const normalized = normalizeRecordTimePunchPayload({
    accountId: input.accountId,
    punchType: input.punchType,
    deviceTime,
    deviceId: input.deviceId,
    appVersion: input.appVersion,
    routeAssignmentId: input.routeAssignmentId,
    lat: input.lat,
    lng: input.lng,
    gpsAccuracyMeters: input.gpsAccuracyMeters,
    gpsPermission: input.gpsPermission,
    offlineCreated: false,
    notes: null,
    metadata: {},
  });
  if (!normalized.ok) {
    // Shared-contract messages are written to be SAFE user copy (never echo values).
    return { ok: false, reason: normalized.message };
  }
  const payload = normalized.payload;
  try {
    const { error } = await client.rpc("record_time_punch", {
      p_account_id: payload.accountId,
      p_punch_type: payload.punchType,
      p_device_time: payload.deviceTime,
      p_device_id: payload.deviceId,
      p_app_version: payload.appVersion,
      p_route_assignment_id: payload.routeAssignmentId,
      p_lat: payload.lat,
      p_lng: payload.lng,
      p_gps_accuracy_meters: payload.gpsAccuracyMeters,
      p_gps_permission: payload.gpsPermission,
      p_offline_created: payload.offlineCreated,
      p_notes: payload.notes,
      p_metadata: payload.metadata,
    });
    if (error) {
      return { ok: false, reason: SAFE_SUBMIT_FAILURE };
    }
    return { ok: true };
  } catch {
    return { ok: false, reason: SAFE_SUBMIT_FAILURE };
  }
}
